import { RedisClientType } from "redis"
import { redisBase } from "./redisBase.js"

/**
 * Extract all possible cache key types from a cache key definition object
 * Recursively extracts string literals and function return types
 *
 * @example
 * ```typescript
 * const CACHE_KEYS = {
 *   users: {
 *     profile: (userId: number) => `user:${userId}:profile` as const,
 *   },
 * } as const;
 *
 * type CacheKey = ExtractCacheKeyType<typeof CACHE_KEYS>;
 * // Result: "user:${number}:profile"
 * ```
 */
export type ExtractCacheKeyType<T> = T extends string
  ? T
  : T extends (...args: any[]) => infer R
  ? R extends string
    ? R
    : never
  : T extends Record<string, any>
  ? { [K in keyof T]: ExtractCacheKeyType<T[K]> }[keyof T]
  : never

interface RedisGenericClientConfig {
  prefix: string
  isEnabled: boolean
}

/**
 * Generic Redis client with optional type-safe cache keys
 * @template TCacheKeys - Optional type for cache keys (defaults to string)
 */
class RedisGenericClient<TCacheKeys = string> {
  private readonly keyPrefix: string
  private readonly isEnabled: boolean

  constructor(config: RedisGenericClientConfig) {
    this.keyPrefix = config.prefix.endsWith(":")
      ? config.prefix
      : `${config.prefix}:`
    this.isEnabled = config.isEnabled
  }

  private addPrefix(key: string): string {
    if (key.startsWith(this.keyPrefix)) {
      return key
    }
    return `${this.keyPrefix}${key}`
  }

  private removePrefix(key: string): string {
    if (key.startsWith(this.keyPrefix)) {
      return key.slice(this.keyPrefix.length)
    }
    return key
  }

  public isClientEnabled(): boolean {
    return this.isEnabled
  }

  public isClientConnected(): boolean {
    if (!this.isEnabled) {
      return false
    }
    return redisBase.isClientConnected()
  }

  public getStatus() {
    return {
      ...redisBase.getStatus(),
      isEnabled: this.isEnabled,
      prefix: this.keyPrefix,
    }
  }

  public async set(key: TCacheKeys, value: string): Promise<string | null> {
    if (!this.isEnabled) {
      return null
    }

    const prefixedKey = this.addPrefix(key as string)
    return redisBase.safeExecute(async (client) => {
      return await client.set(prefixedKey, value)
    }) as Promise<string | null>
  }

  public async setObj<T>(key: TCacheKeys, value: T): Promise<string | null> {
    if (!this.isEnabled) {
      return null
    }

    try {
      const serializedValue = JSON.stringify(value)
      const prefixedKey = this.addPrefix(key as string)
      return redisBase.safeExecute(async (client) => {
        return await client.set(prefixedKey, serializedValue)
      }) as Promise<string | null>
    } catch (error) {
      console.error("Error serializing object for Redis:", error)
      return null
    }
  }

  public async setEx(
    key: TCacheKeys,
    seconds: number,
    value: string
  ): Promise<string | null> {
    if (!this.isEnabled) {
      return null
    }

    const prefixedKey = this.addPrefix(key as string)
    return redisBase.safeExecute(async (client) => {
      return await client.setEx(prefixedKey, seconds, value)
    }) as Promise<string | null>
  }

  public async setObjEx<T>(
    key: TCacheKeys,
    seconds: number,
    value: T
  ): Promise<string | null> {
    if (!this.isEnabled) {
      return null
    }

    try {
      const serializedValue = JSON.stringify(value)
      const prefixedKey = this.addPrefix(key as string)
      return redisBase.safeExecute(async (client) => {
        return await client.setEx(prefixedKey, seconds, serializedValue)
      }) as Promise<string | null>
    } catch (error) {
      console.error("Error serializing object for Redis setEx:", error)
      return null
    }
  }

  public async get(key: TCacheKeys): Promise<string | null> {
    if (!this.isEnabled) {
      return null
    }

    const prefixedKey = this.addPrefix(key as string)
    return redisBase.safeExecute(async (client) => {
      return await client.get(prefixedKey)
    }) as Promise<string | null>
  }

  public async getObj<T>(key: TCacheKeys): Promise<T | null> {
    if (!this.isEnabled) {
      return null
    }

    try {
      const prefixedKey = this.addPrefix(key as string)
      const serializedValue = (await redisBase.safeExecute(async (client) => {
        return await client.get(prefixedKey)
      })) as string | null

      if (serializedValue === null) {
        return null
      }

      return JSON.parse(serializedValue) as T
    } catch (error) {
      console.error("Error deserializing object from Redis:", error)
      return null
    }
  }

  public async del(key: TCacheKeys): Promise<number | null> {
    if (!this.isEnabled) {
      return null
    }

    const prefixedKey = this.addPrefix(key as string)
    return redisBase.safeExecute(async (client) => {
      return await client.del(prefixedKey)
    }) as Promise<number | null>
  }

  public async delMultiple(keys: TCacheKeys[]): Promise<number | null> {
    if (!this.isEnabled) {
      return null
    }

    const prefixedKeys = keys.map((key) => this.addPrefix(key as string))
    return redisBase.safeExecute(async (client) => {
      return await client.del(prefixedKeys)
    }) as Promise<number | null>
  }

  /**
   * Delete all keys matching a pattern (Production-safe using SCAN)
   * @param pattern - Pattern to match (e.g., "games:*" to delete all keys starting with "games:")
   * @param batchSize - Number of keys to scan per iteration (default: 100)
   * @returns Number of keys deleted, or null if client is disabled
   *
   * @example
   * ```typescript
   * // Delete all game-related keys
   * await cache.delByPattern("games:*");
   *
   * // With type-safe patterns
   * await cache.delByPattern(CACHE_KEYS.games._pattern);
   * ```
   */
  public async delByPattern(
    pattern: TCacheKeys | string,
    batchSize: number = 100
  ): Promise<number | null> {
    if (!this.isEnabled) {
      return null
    }

    const prefixedPattern = this.addPrefix(pattern as string)
    return redisBase.safeExecute(async (client) => {
      let totalDeleted = 0
      const keysToDelete: string[] = []

      // Use scanIterator for cleaner async iteration
      for await (const key of client.scanIterator({
        MATCH: prefixedPattern,
        COUNT: batchSize,
      })) {
        // scanIterator returns string keys, not arrays
        if (typeof key === "string") {
          keysToDelete.push(key)
        } else if (Array.isArray(key)) {
          keysToDelete.push(...key)
        }

        // Delete in batches to avoid blocking Redis
        if (keysToDelete.length >= batchSize) {
          const deleted = await client.del(keysToDelete)
          totalDeleted += deleted
          keysToDelete.length = 0 // Clear array
        }
      }

      // Delete remaining keys
      if (keysToDelete.length > 0) {
        const deleted = await client.del(keysToDelete)
        totalDeleted += deleted
      }

      return totalDeleted
    }) as Promise<number | null>
  }

  public async exists(key: TCacheKeys): Promise<number | null> {
    if (!this.isEnabled) {
      return null
    }

    const prefixedKey = this.addPrefix(key as string)
    return redisBase.safeExecute(async (client) => {
      return await client.exists(prefixedKey)
    }) as Promise<number | null>
  }

  public async expire(
    key: TCacheKeys,
    seconds: number
  ): Promise<number | null> {
    if (!this.isEnabled) {
      return null
    }

    const prefixedKey = this.addPrefix(key as string)
    return redisBase.safeExecute(async (client) => {
      return await client.expire(prefixedKey, seconds)
    }) as Promise<number | null>
  }

  public async ttl(key: TCacheKeys): Promise<number | null> {
    if (!this.isEnabled) {
      return null
    }

    const prefixedKey = this.addPrefix(key as string)
    return redisBase.safeExecute(async (client) => {
      return await client.ttl(prefixedKey)
    }) as Promise<number | null>
  }

  public async incr(key: TCacheKeys): Promise<number | null> {
    if (!this.isEnabled) {
      return null
    }

    const prefixedKey = this.addPrefix(key as string)
    return redisBase.safeExecute(async (client) => {
      return await client.incr(prefixedKey)
    }) as Promise<number | null>
  }

  public async incrBy(
    key: TCacheKeys,
    increment: number
  ): Promise<number | null> {
    if (!this.isEnabled) {
      return null
    }

    const prefixedKey = this.addPrefix(key as string)
    return redisBase.safeExecute(async (client) => {
      return await client.incrBy(prefixedKey, increment)
    }) as Promise<number | null>
  }

  public async decr(key: TCacheKeys): Promise<number | null> {
    if (!this.isEnabled) {
      return null
    }

    const prefixedKey = this.addPrefix(key as string)
    return redisBase.safeExecute(async (client) => {
      return await client.decr(prefixedKey)
    }) as Promise<number | null>
  }

  public async keys(pattern: string): Promise<string[] | null> {
    if (!this.isEnabled) {
      return null
    }

    const prefixedPattern = this.addPrefix(pattern)
    const result = await redisBase.safeExecute(async (client) => {
      return await client.keys(prefixedPattern)
    })

    // Remove prefix from returned keys for cleaner usage
    if (result) {
      return result.map((key) => this.removePrefix(key))
    }
    return result
  }

  public async hSet(
    key: TCacheKeys,
    field: string,
    value: string
  ): Promise<number | null> {
    if (!this.isEnabled) {
      return null
    }

    const prefixedKey = this.addPrefix(key as string)
    return redisBase.safeExecute(async (client) => {
      return await client.hSet(prefixedKey, field, value)
    }) as Promise<number | null>
  }

  public async hGet(key: TCacheKeys, field: string): Promise<string | null> {
    if (!this.isEnabled) {
      return null
    }

    const prefixedKey = this.addPrefix(key as string)
    return redisBase.safeExecute(async (client) => {
      return await client.hGet(prefixedKey, field)
    }) as Promise<string | null>
  }

  public async hGetAll(
    key: TCacheKeys
  ): Promise<Record<string, string> | null> {
    if (!this.isEnabled) {
      return null
    }

    const prefixedKey = this.addPrefix(key as string)
    return redisBase.safeExecute(async (client) => {
      return await client.hGetAll(prefixedKey)
    }) as Promise<Record<string, string> | null>
  }

  public async hDel(
    key: TCacheKeys,
    fields: string | string[]
  ): Promise<number | null> {
    if (!this.isEnabled) {
      return null
    }

    const prefixedKey = this.addPrefix(key as string)
    return redisBase.safeExecute(async (client) => {
      return await client.hDel(prefixedKey, fields)
    }) as Promise<number | null>
  }

  public async lPush(
    key: TCacheKeys,
    ...elements: string[]
  ): Promise<number | null> {
    if (!this.isEnabled) {
      return null
    }

    const prefixedKey = this.addPrefix(key as string)
    return redisBase.safeExecute(async (client) => {
      return await client.lPush(prefixedKey, elements)
    }) as Promise<number | null>
  }

  public async rPush(
    key: TCacheKeys,
    ...elements: string[]
  ): Promise<number | null> {
    if (!this.isEnabled) {
      return null
    }

    const prefixedKey = this.addPrefix(key as string)
    return redisBase.safeExecute(async (client) => {
      return await client.rPush(prefixedKey, elements)
    }) as Promise<number | null>
  }

  public async lPop(key: TCacheKeys): Promise<string | null> {
    if (!this.isEnabled) {
      return null
    }

    const prefixedKey = this.addPrefix(key as string)
    return redisBase.safeExecute(async (client) => {
      return await client.lPop(prefixedKey)
    }) as Promise<string | null>
  }

  public async rPop(key: TCacheKeys): Promise<string | null> {
    if (!this.isEnabled) {
      return null
    }

    const prefixedKey = this.addPrefix(key as string)
    return redisBase.safeExecute(async (client) => {
      return await client.rPop(prefixedKey)
    }) as Promise<string | null>
  }

  public async lRange(
    key: TCacheKeys,
    start: number,
    stop: number
  ): Promise<string[] | null> {
    if (!this.isEnabled) {
      return null
    }

    const prefixedKey = this.addPrefix(key as string)
    return redisBase.safeExecute(async (client) => {
      return await client.lRange(prefixedKey, start, stop)
    }) as Promise<string[] | null>
  }

  public async sAdd(
    key: TCacheKeys,
    ...members: string[]
  ): Promise<number | null> {
    if (!this.isEnabled) {
      return null
    }

    const prefixedKey = this.addPrefix(key as string)
    return redisBase.safeExecute(async (client) => {
      return await client.sAdd(prefixedKey, members)
    }) as Promise<number | null>
  }

  public async sMembers(key: TCacheKeys): Promise<string[] | null> {
    if (!this.isEnabled) {
      return null
    }

    const prefixedKey = this.addPrefix(key as string)
    return redisBase.safeExecute(async (client) => {
      return await client.sMembers(prefixedKey)
    }) as Promise<string[] | null>
  }

  public async sRem(
    key: TCacheKeys,
    ...members: string[]
  ): Promise<number | null> {
    if (!this.isEnabled) {
      return null
    }

    const prefixedKey = this.addPrefix(key as string)
    return redisBase.safeExecute(async (client) => {
      return await client.sRem(prefixedKey, members)
    }) as Promise<number | null>
  }

  public async safeExecute<T>(
    operation: (
      client: RedisClientType,
      addPrefix: (key: string) => string
    ) => Promise<T>
  ): Promise<T | null> {
    if (!this.isEnabled) {
      return null
    }

    return redisBase.safeExecute(async (client) => {
      return await operation(client, this.addPrefix.bind(this))
    })
  }
}

export { RedisGenericClient }

type TCreateRedisClient = {
  prefix: string
  isEnabled?: boolean
}

/**
 * Create a Redis client instance
 *
 * @example
 * ```typescript
 * // Without type safety (accepts any string)
 * const cache = createRedisGenericClient({ prefix: "myapp" });
 * await cache.set("any:key", "value");
 *
 * // With type safety (only accepts defined cache keys)
 * const CACHE_KEYS = {
 *   users: {
 *     profile: (userId: number) => `user:${userId}:profile` as const,
 *   },
 * } as const;
 *
 * type CacheKey = ExtractCacheKeyType<typeof CACHE_KEYS>;
 * const cache = createRedisGenericClient<CacheKey>({ prefix: "myapp" });
 * await cache.set(CACHE_KEYS.users.profile(123), "value"); // ✅ Valid
 * await cache.set("random:key", "value"); // ❌ Type error
 * ```
 */
export const createRedisGenericClient = <TCacheKeys = string>({
  prefix,
  isEnabled = true,
}: TCreateRedisClient): RedisGenericClient<TCacheKeys> => {
  return new RedisGenericClient<TCacheKeys>({ prefix, isEnabled })
}
